import { ZipApi } from '../../lib/ZipApi';
import { directoryStore } from '../DirectoryStore';
import { ObjectDefinition } from '../../types/DataSyncItem';
import { DataSyncObjectsStore } from './DataSyncObjectsStore';
import { getApiErrorMessage } from '../../lib/ZipApiError';
import { showToasts } from '../../lib/toaster';

export default class DataSyncHubAppObjectsStore extends DataSyncObjectsStore {

  protected async fetchSourceObjectDefinitions(): Promise<ObjectDefinition[]> {
    if (!directoryStore.trackerId || !this.instanceId) {
      return [];
    }

    const trackerId = directoryStore.trackerId;
    const instanceId = this.instanceId as string;

    const sources = await ZipApi.listAppSources(trackerId, instanceId);

    // One unreadable schema must not empty the whole dropdown, so skip it and keep the rest.
    const skipped: string[] = [];
    let firstError: any;
    const results = await Promise.all(
      sources.map(async (source) => {
        try {
          const schema = await ZipApi.listHubAppSourceSchema(
            trackerId,
            instanceId,
            source.name
          );

          return schema ? {
            object_name: source.name,
            fields: schema.fields,
            locale_config: schema.locale_config,
          } : null;
        } catch (error: any) {
          skipped.push(source.name);
          firstError = firstError ?? error;
          return null;
        }
      })
    );

    // Every object failed, so the dropdown would be empty and the user cannot proceed. Let it
    // propagate to handleSourceFetchError rather than downgrading it to a skip warning.
    if (firstError && skipped.length === sources.length) {
      throw firstError;
    }

    this.warnAboutSkippedObjects(skipped, 'source');
    return results.filter(Boolean) as ObjectDefinition[];
  }

  protected async fetchDestinationObjectDefinitions(): Promise<ObjectDefinition[]> {
    if (!directoryStore.trackerId || !this.instanceId) {
      return [];
    }

    const trackerId = directoryStore.trackerId as string;
    const instanceId = this.instanceId as string;

    const destinations = await ZipApi.listAppDestinations(
      trackerId,
      instanceId
    );

    const skipped: string[] = [];
    let firstError: any;
    const schemas = await Promise.all(
      destinations.map(async (destination) => {
        try {
          const schema = await ZipApi.listHubAppDestinationSchema(
            trackerId,
            instanceId,
            destination
          );
          return schema && schema.fields
            ? ({
                object_name: destination,
                fields: schema.fields,
              } as ObjectDefinition)
            : null;
        } catch (error: any) {
          skipped.push(destination);
          firstError = firstError ?? error;
          return null;
        }
      })
    );

    if (firstError && skipped.length === destinations.length) {
      throw firstError;
    }

    this.warnAboutSkippedObjects(skipped, 'destination');
    return schemas.filter(Boolean) as ObjectDefinition[];
  }

  private warnAboutSkippedObjects(skipped: string[], kind: 'source' | 'destination'): void {
    if (skipped.length === 0) {
      return;
    }

    // Resolution order is nondeterministic; sort so the message is stable.
    showToasts([{
      intent: 'warning',
      message: `Some ${kind} objects couldn't be loaded and were skipped: ${skipped.sort().join(', ')}.`
    }]);
  }

  protected async handleSourceFetchError(error: any): Promise<void> {
    const message = await getApiErrorMessage(error, 'Unable to fetch source app schema');
    showToasts([{intent: 'danger', message}]);
  }

  protected async handleDestinationFetchError(error: any): Promise<void> {
    const message = await getApiErrorMessage(error, 'Unable to fetch destination app schema');
    showToasts([{intent: 'danger', message}]);
  }
}
